import React, { useEffect, useState, useRef } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Toolbar from '@mui/material/Toolbar';
import { Button, Dialog, DialogTitle, DialogContent, DialogActions, Stack } from '@mui/material';
import Paper from '@mui/material/Paper';
import Checkbox from '@mui/material/Checkbox';
import {
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  Input,
  InputLabel,
  InputAdornment,
  TextField,
  Select,
  MenuItem
} from '@mui/material';
import { Add } from '@mui/icons-material';
import {
  addProduct,
  getPointOfSales,
  getProducts,
  updateProduct
} from 'actions/productActions';
import { getPartners } from 'actions/partnerActions';
import { arrayToKeys } from 'utils/parsingUtils';
import { debounce } from '@mui/material';
import { Pagination } from '@mui/lab';
import { Notify, SUCCESS_NOTIF, ERROR_NOTIF } from './components/Notify';
import ImageUploadForm from './components/ImageUploadForm';
import { uploadImage } from 'services/firebase';
import { TableLoading } from './components/TableLoading';
import { orderfulApi } from '../../services/api-instances';

// Extracted components
import { UpdateTableFormHeader } from './components/UpdateTableFormHeader';
import { Sidebar } from './components/Sidebar';
import { ImageCell } from './components/ImageCell';
import { StockStatus } from './components/StockStatus';
import { FilterSelect } from './components/FilterSelect';
import { EditFieldsButton } from './components/EditFieldsButton';
import { BundleFilter } from './components/BundleFilter';
import { StoreFilter } from './components/StoreFilter';
import { PriceFilter } from './components/PriceFilter';
import { StockFilter } from './components/StockFilter';
import { WeightFilter } from './components/WeightFilter';
import { SnoozeFilter } from './components/SnoozeFilter';

// Re-export for backward compatibility
export { UpdateTableFormHeader } from './components/UpdateTableFormHeader';
export { Sidebar } from './components/Sidebar';
export { EditFieldsButton } from './components/EditFieldsButton';

const headCells = [
  {
    id: 'product',
    numeric: false,
    disablePadding: true,
    label: 'Product'
  },
  {
    id: 'store',
    numeric: true,
    disablePadding: false,
    label: 'Store'
  },
  {
    id: 'stock',
    numeric: true,
    disablePadding: false,
    label: 'Stock Status'
  },
  {
    id: 'price',
    numeric: true,
    disablePadding: false,
    label: 'Price'
  },
  {
    id: 'bundle',
    numeric: true,
    disablePadding: false,
    label: 'Bundle'
  },
  {
    id: 'weight',
    numeric: true,
    disablePadding: false,
    label: 'Weight'
  },
  {
    id: 'volume',
    numeric: true,
    disablePadding: false,
    label: 'Volume'
  },
  {
    id: 'depth',
    numeric: true,
    disablePadding: false,
    label: 'Depth'
  }
  ,
  {
    id: 'actions',
    numeric: false,
    disablePadding: false,
    label: 'Actions'
  }
];

function EnhancedTableHead(props) {
  const { order, orderBy } = props;

  return (
    <TableHead>
      <TableRow>
        <TableCell align={'left'}></TableCell>
        {headCells.map(headCell => (
          <TableCell
            key={headCell.id}
            align={'left'}
            sortDirection={orderBy === headCell.id ? order : false}>
            {headCell.label}
          </TableCell>
        ))}
      </TableRow>
    </TableHead>
  );
}

const SearchInput = ({ onSearch }) => {
  return (
    <input
      type="textfield"
      placeholder="Search products"
      onChange={evt => {
        onSearch(evt.currentTarget.value);
      }}
      style={{
        height: 30,
        borderRadius: 15,
        background: '#F7F7FC',
        border: '1px solid #D9DBE9',
        alignItems: 'center',
        padding: '12px 24px 12px 16px'
      }}
    />
  );
};

const STORE_FILTER = 'STORE_FILTER';
const STOCK_FILTER = 'STOCK_FILTER';
const PRICE_FILTER = 'PRICE_FILTER';
const BUNDLE_FILTER = 'BUNDLE_FILTER';
const WEIGHT_FILTER = 'WEIGHT_FILTER';
const SNOOZE_FILTER = 'SNOOZE_FILTER';

const EnhancedTableToolbar = props => {
  const { onSearch, openFilter } = props;

  return (
    <>
      <Grid
        container
        justifyContent="space-between"
        alignItems="center"
        style={{ padding: '25px 15px 0px 15px', gap: 16 }}>
        <Grid item>
          <h3 style={{ fontSize: 20, fontFamily: 'Arial', margin: 0 }}>Inventory</h3>
        </Grid>
        <Grid item style={{ marginLeft: 'auto' }}>
          <button
            onClick={props.onAddItem}
            style={{
              border: '1px solid #0583FF',
              height: 37.5,
              borderRadius: 18.75,
              display: 'flex',
              alignItems: 'center',
              paddingLeft: 12,
              paddingRight: 12,
              backgroundColor: 'white',
              cursor: 'pointer'
            }}>
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'center',
                color: '#0583FF',
                gap: 6
              }}>
              <Add />
              <span style={{ fontWeight: '600' }}>Add Product</span>
            </div>
          </button>
        </Grid>
      </Grid>
      <Toolbar
        style={{ padding: '0px 15px 15px 15px', overflowX: 'auto' }}
        sx={{
          pl: { sm: 2 },
          pr: { xs: 1, sm: 1 }
        }}>
        <SearchInput onSearch={onSearch} />
        <FilterSelect
          title="Stores"
          onSelect={() => {
            openFilter(STORE_FILTER);
          }}
        />
        <FilterSelect
          title="Stock status"
          onSelect={() => {
            openFilter(STOCK_FILTER);
          }}
        />
        <FilterSelect
          title="Price"
          onSelect={() => {
            openFilter(PRICE_FILTER);
          }}
        />
        <FilterSelect
          title="Bundle"
          onSelect={() => {
            openFilter(BUNDLE_FILTER);
          }}
        />
        <FilterSelect
          title="Weight status"
          onSelect={() => {
            openFilter(WEIGHT_FILTER);
          }}
        />
        <FilterSelect
          title="Snooze status"
          onSelect={() => {
            openFilter(SNOOZE_FILTER);
          }}
        />
      </Toolbar>
    </>
  );
};

const UpdateItem = ({
  onClose,
  onSubmit,
  partners,
  pointOfSales,
  products,
  edit
}) => {
  const [productName, setProductName] = useState('');
  const [imageURL, setImageURL] = useState('');
  const [blob, setBlob] = useState('');
  const [weight, setWeight] = useState();
  const [volume, setVolume] = useState();
  const [depth, setDepth] = useState();
  const [inStock, setInStock] = useState(true);
  const [price, setPrice] = useState();
  const [category, setCategory] = useState();
  const [partnerId, setPartnerId] = useState('');
  const [posId, setPosId] = useState('');
  const [productId, setProductId] = useState('');
  const [platformItemId, setPlatformItemId] = useState('');

  const originalImageRef = useRef();

  useEffect(() => {
    if (edit && products) {
      const editProduct = products[0];
      const posId = pointOfSales[editProduct.storeName].id;
      setProductName(editProduct.productName);
      setOriginalImage(editProduct.image);
      setWeight(editProduct.weight);
      setVolume(editProduct.volume);
      setPrice(editProduct.price);
      setDepth(editProduct.depth);
      setPartnerId(editProduct.partnerId);
      setPosId(posId);
      setInStock(editProduct.inStock);
      setProductId(editProduct.productId);
      setPlatformItemId(editProduct.platformItemId);
    }
  }, []);

  const setOriginalImage = image => {
    setImageURL(image);
    originalImageRef.current = image;
  };

  const submit = async () => {
    try {
      let imageURLToSend = imageURL;
      if (originalImageRef.current !== imageURL) {
        imageURLToSend = await uploadNewImage(blob);
      }

      onSubmit({
        partnerId,
        productId,
        productName,
        image: imageURLToSend,
        platformItemId,
        weight: parseFloat(weight),
        volume: parseFloat(volume),
        depth: parseFloat(depth),
        category,
        inStock,
        price: parseFloat(price),
        posId
      });
    } catch (err) {
      console.log(err);
    }
  };

  const uploadNewImage = async blob => {
    return new Promise((resolve, reject) => {
      const uploadImageTask = uploadImage(blob.name, blob);
      uploadImageTask.on(
        'state_changed',
        () => {},
        err => reject(err),
        async snapshot => {
          const downloadURL = await uploadImageTask.snapshot.ref.getDownloadURL();
          resolve(downloadURL);
        }
      );
    });
  };

  const onChangePartner = evt => {
    setPartnerId(evt.target.value);
  };

  const onChangePosId = evt => {
    setPosId(evt.target.value);
  };

  const onChangeImage = ({ blob, url }) => {
    setBlob(blob);
    setImageURL(url);
  };

  return (
    <Sidebar
      title={edit ? 'Edit Item' : 'Add Item'}
      onClose={onClose}
      onDone={submit}>
      <div style={{ height: '81%', overflowY: 'auto' }}>
        <div style={{ padding: 20 }}>
          <ImageUploadForm imageUrl={imageURL} onChangeImage={onChangeImage} />
        </div>
        <TextField
          placeholder="Product Name"
          fullWidth
          style={{ padding: 20, marginTop: 30 }}
          value={productName}
          onChange={evt => {
            setProductName(evt.target.value);
          }}
        />
        <FormControl fullWidth variant="standard" style={{ padding: 20 }}>
          <Input
            endAdornment={<InputAdornment position="end">g</InputAdornment>}
            inputProps={{ 'aria-label': 'weight' }}
            fullWidth
            value={weight}
            type="number"
            onChange={evt => {
              setWeight(evt.target.value);
            }}
          />
          <FormHelperText id="standard-weight-helper-text">
            Weight
          </FormHelperText>
        </FormControl>
        <FormControl fullWidth variant="standard" style={{ padding: 20 }}>
          <Input
            endAdornment={<InputAdornment position="end">cm3</InputAdornment>}
            fullWidth
            value={volume}
            type="number"
            onChange={evt => {
              if (evt.target.value) {
                setVolume(evt.target.value);
              }
            }}
          />
          <FormHelperText id="standard-weight-helper-text">
            Volume
          </FormHelperText>
        </FormControl>
        <FormControl fullWidth variant="standard" style={{ padding: 20 }}>
          <Input
            endAdornment={<InputAdornment position="end">cm</InputAdornment>}
            value={depth}
            onChange={evt => {
              if (evt.target.value) {
                setDepth(evt.target.value);
              }
            }}
            inputProps={{ 'aria-label': 'weight' }}
            fullWidth
          />
          <FormHelperText id="standard-weight-helper-text">
            Depth
          </FormHelperText>
        </FormControl>
        <FormControl fullWidth variant="standard" style={{ padding: 20 }}>
          <Input
            startAdornment={<InputAdornment position="start">€</InputAdornment>}
            inputProps={{ 'aria-label': 'weight' }}
            fullWidth
            value={price}
            type="number"
            onChange={evt => {
              if (evt.target.value) {
                setPrice(evt.target.value);
              }
            }}
          />
          <FormHelperText id="standard-weight-helper-text">
            Price
          </FormHelperText>
        </FormControl>
        <TextField
          placeholder="Category"
          value={category}
          onChange={evt => {
            setCategory(evt.target.value);
          }}
          fullWidth
          style={{ padding: 20 }}
        />
        <div style={{ padding: 20 }}>
          <FormControl fullWidth>
            <InputLabel>Partner</InputLabel>
            <Select
              value={partnerId}
              label="Partner"
              onChange={onChangePartner}>
              {partners.map(partner => {
                return (
                  <MenuItem key={partner.id} value={partner.id}>
                    {partner.partnerName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
        <div style={{ padding: 20 }}>
          <FormControl fullWidth>
            <InputLabel>Point of sale</InputLabel>
            <Select value={posId} label="Partner" onChange={onChangePosId}>
              {Object.keys(pointOfSales).map(posName => {
                return (
                  <MenuItem
                    key={`point-of-sale-${posName}`}
                    value={pointOfSales[posName].id}>
                    {posName}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
        </div>
        <FormControlLabel
          control={<Checkbox defaultChecked />}
          onChange={(_evt, val) => {
            setInStock(val);
          }}
          value={inStock}
          label="In Stock"
          style={{ padding: 20 }}
        />
      </div>
    </Sidebar>
  );
};


export default function InventoryTable({ cellId }) {
  const [selected, setSelected] = React.useState([]);
  const [fetchingProducts, setFetchingProducts] = useState(true);
  const [products, setProducts] = useState([]);
  const [bundles, setBundles] = useState([]);
  const [pointOfSales, setPointOfSales] = useState({});
  const [partners, setPartners] = useState();
  const [showEditItems, setShowEditItems] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [filterType, setFilterType] = useState();
  const [page, setPage] = useState(1);
  const [selectedStores, setSelectedStores] = useState([]);
  const [selectedBundles, setSelectedBundles] = useState([]);
  const [selectedStockFilters, setSelectedStockFilters] = useState([
    true,
    true
  ]);
  const [selectedWeightFilters, setSelectedWeightFilters] = useState([
    true,
    true
  ]);
  const [selectedSnoozeFilters, setSelectedSnoozeFilters] = useState([
    true,
    true
  ]);
  const [selectedPriceValues, setSelectedPriceValues] = useState([0, 100]);
  const [notification, setNotification] = useState();
  const [searchTerm, setSearchTerm] = useState();
  const [isSnoozeOpen, setIsSnoozeOpen] = useState(false);
  const [snoozeTarget, setSnoozeTarget] = useState(null);

  const snoozeOptions = [
    { label: '30 minutes', value: '30mins' },
    { label: '1 hour', value: '1hour' },
    { label: '24 hours', value: '24hours' },
    { label: 'End of Day', value: 'endofday' },
    { label: '1 week', value: '1week' }
  ];

  const productsRef = useRef([]);

  const refreshProducts = async () => {
    try {
      setFetchingProducts(true);
      const partners = await getPartners();
      setPartners(partners);
      const stores = await getPointOfSales(cellId);
      const storesKeyVal = arrayToKeys('posName', stores);
      setPointOfSales(storesKeyVal.objectWithKeys);

      const products = await getProducts(cellId);
      const { parsedProductList, bundles } = parseProductsToList(products);
      setProducts(parsedProductList);
      productsRef.current = parsedProductList;
      setBundles(bundles);
      runFilter();
      if (searchTerm) {
        search(searchTerm);
      }
      setFetchingProducts(false);
    } catch (err) {
      setFetchingProducts(false);
    }
  };

  useEffect(() => {
    refreshProducts();
  }, [cellId]);

  // Auto-run filters when filter values change
  useEffect(() => {
    if (productsRef.current.length > 0) {
      runFilter();
    }
  }, [selectedStores, selectedBundles, selectedStockFilters, selectedWeightFilters, selectedSnoozeFilters, selectedPriceValues]);

  const storeFilterCheck = storeName => {
    if (selectedStores.length > 0) {
      return selectedStores.includes(storeName);
    } else {
      return true;
    }
  };

  const bundleFilterCheck = bundleName => {
    if (selectedBundles.length > 0) {
      return selectedBundles.includes(bundleName);
    } else {
      return true;
    }
  };

  const priceFilterCheck = price => {
    return price >= selectedPriceValues[0] && price < selectedPriceValues[1];
  };

  const stockFilterCheck = stockStatus => {
    if (stockStatus) {
      return selectedStockFilters[0];
    } else {
      return selectedStockFilters[1];
    }
  };

  const weightFilterCheck = weight => {
    const hasWeight = weight !== null && weight !== undefined && weight !== '' && weight > 0;
    if (hasWeight) {
      return selectedWeightFilters[0]; // Has weight
    } else {
      return selectedWeightFilters[1]; // No weight
    }
  };

  const snoozeFilterCheck = snoozeUntil => {
    const isSnoozed = snoozeUntil !== null && snoozeUntil !== undefined;
    if (isSnoozed) {
      return selectedSnoozeFilters[0]; // Snoozed
    } else {
      return selectedSnoozeFilters[1]; // Not snoozed
    }
  };

  const runFilter = () => {
    const filteredProducts = productsRef.current.filter(product => {
      let passStoreFilter = storeFilterCheck(product.storeName);
      let passBundleFilter = bundleFilterCheck(product.bundleName);
      let passPriceFilter = priceFilterCheck(product.price);
      let passWeightFilter = weightFilterCheck(product.weight);
      let passSnoozeFilter = snoozeFilterCheck(product.snoozeUntil);
      let passStockStatusFilter = stockFilterCheck(product.inStock);
      return (
        passBundleFilter &&
        passStoreFilter &&
        passPriceFilter &&
        passStockStatusFilter &&
        passWeightFilter &&
        passSnoozeFilter
      );
    });

    setProducts(filteredProducts);
  };

  const parseProductsToList = products => {
    const parsedProductList = [];
    const bundles = [];
    products.forEach(product => {
      const isBundle = product.platformItemProducts.length > 1;
      if (isBundle) bundles.push(product.platformItemName);
      product.platformItemProducts.forEach(platformItemProduct => {
        const partnerProduct = platformItemProduct.partnerProduct;
        const inStock =
          platformItemProduct.inStock !== null
            ? platformItemProduct.inStock
            : false;
        const productId = partnerProduct.productId;
        const parsedProduct = {
          ...partnerProduct,
          bundleName: isBundle ? product.platformItemName : false,
          storeName: product.pointOfSale.posName,
          inStock,
          price: platformItemProduct.price || 0,
          currency: platformItemProduct.currency,
          platformItemId: product.platformItemId || product.id,
          productId,
          snoozeUntil: product.snoozeUntil || null
        };
        parsedProductList.push(parsedProduct);
      });
    });
    return { parsedProductList, bundles };
  };

  const openFilterType = filter => {
    setFilterType(filter);
  };

  const search = searchTerm => {
    const filteredProducts = productsRef.current.filter(p => {
      return p.productName.toLowerCase().includes(searchTerm.toLowerCase());
    });
    setSearchTerm(searchTerm);
    setProducts(filteredProducts);
    setPage(1);
    setSelected([]);
    setShowEditItems(false);
  };

  const onSearch = debounce(search, 300);

  const openSnoozeForRow = (row) => {
    setSnoozeTarget(row);
    setIsSnoozeOpen(true);
  };

  const submitSnoozeForRow = async (option) => {
    try {
      if (!snoozeTarget) return;
      const pos = pointOfSales[snoozeTarget.storeName];
      if (!pos?.id) {
        setNotification({ type: ERROR_NOTIF, message: 'Missing merchant id for this item' });
        setIsSnoozeOpen(false);
        setSnoozeTarget(null);
        return;
      }

      if (!snoozeTarget?.platformItemId) {
        setNotification({ type: ERROR_NOTIF, message: 'Missing platformItemId on item' });
        setIsSnoozeOpen(false);
        setSnoozeTarget(null);
        return;
      }

      const elements = [
        {
          MenuElementId: snoozeTarget.platformItemId,
          MenuElementType: 'Item',
          Name: snoozeTarget.productName,
        },
      ];

      await orderfulApi.bulkShowHideMenusForMerchant(
        pos.id,
        false,
        elements,
        { snooze: option.value }
      );

      setIsSnoozeOpen(false);
      setSnoozeTarget(null);
      await refreshProducts();
      setNotification({ type: SUCCESS_NOTIF, message: `Snoozed ${elements[0].Name} for ${option.label}` });
    } catch (err) {
      setNotification({ type: ERROR_NOTIF, message: 'Failed to snooze item' });
    }
  };

  const unsnoozeRow = async (row) => {
    try {
      const pos = pointOfSales[row.storeName];
      if (!pos?.id) {
        setNotification({ type: ERROR_NOTIF, message: 'Missing merchant id for this item' });
        return;
      }
      if (!row?.platformItemId) {
        setNotification({ type: ERROR_NOTIF, message: 'Missing platformItemId on item' });
        return;
      }
      const elements = [
        { MenuElementId: row.platformItemId, MenuElementType: 'Item', Name: row.productName }
      ];
      await orderfulApi.bulkShowHideMenusForMerchant(
        pos.id,
        true,
        elements,
        {}
      );
      await refreshProducts();
      setNotification({ type: SUCCESS_NOTIF, message: `Unsnoozed ${row.productName}` });
    } catch (err) {
      setNotification({ type: ERROR_NOTIF, message: 'Failed to unsnooze item' });
    }
  };

  const handleClick = (event, selectedProduct) => {
    try {
      const selectedIndex = selected.findIndex(
        p => p.productName === selectedProduct.productName
      );
      let newSelected = [];
      if (selectedIndex === -1) {
        newSelected = newSelected.concat(selected, selectedProduct);
      } else if (selectedIndex === 0) {
        newSelected = newSelected.concat(selected.slice(1));
      } else if (selectedIndex === selected.length - 1) {
        newSelected = newSelected.concat(selected.slice(0, -1));
      } else if (selectedIndex > 0) {
        newSelected = newSelected.concat(
          selected.slice(0, selectedIndex),
          selected.slice(selectedIndex + 1)
        );
      }
      setSelected(newSelected);
    } catch (err) {
      console.log(err);
    }
  };

  const onAddProduct = async product => {
    setShowAddItem(false);
    try {
      if (product.id) {
        delete product.id;
      }
      await addProduct(product);
      refreshProducts();
      launchNotification({ type: SUCCESS_NOTIF, message: 'Added sucessfully' });
    } catch (err) {
      launchNotification({
        type: ERROR_NOTIF,
        message: 'Failed to add product'
      });
    }
  };

  const onEditProduct = async product => {
    setSelected([]);
    setShowEditItems(false);
    try {
      await updateProduct(product);
      refreshProducts();
      launchNotification({
        type: SUCCESS_NOTIF,
        message: 'Updated sucessfully'
      });
    } catch (err) {
      launchNotification({
        type: ERROR_NOTIF,
        message: 'Failed to update product'
      });
    }
  };

  const launchNotification = notification => {
    setNotification(notification);
    setTimeout(() => {
      setNotification(false);
    }, 2000);
  };

  const closeFilter = () => {
    setFilterType(false);
  };

  const isSelected = name => {
    return selected.findIndex(p => name === p.productName) !== -1;
  };

  const productRowsStartIndex = (page - 1) * 50;
  const productRowsEndIndex = productRowsStartIndex + 50;
  return (
    <div sx={{ width: '100%' }}>
      <Paper
        sx={{ mb: 2 }}
        style={{ position: 'relative', top: 0, left: 0, width: '100%' }}>
        <EnhancedTableToolbar
          numSelected={selected.length}
          onAddItem={() => {
            setShowAddItem(true);
          }}
          openFilter={openFilterType}
          onSearch={onSearch}
        />
        <TableContainer
          style={{
            height: Math.round(window.innerHeight * 0.65),
            overflow: 'auto'
          }}>
          <Table sx={{ minWidth: 750 }} size={'medium'}>
            <EnhancedTableHead />
            <TableBody style={{ height: 500, overflowY: 'auto' }}>
              {fetchingProducts && (
                <>
                  <TableLoading />
                  <TableLoading />
                  <TableLoading />
                  <TableLoading />
                  <TableLoading />
                  <TableLoading />
                  <TableLoading />
                  <TableLoading />
                  <TableLoading />
                  <TableLoading />
                </>
              )}
              {!fetchingProducts &&
                products
                  .slice(productRowsStartIndex, productRowsEndIndex)
                  .map((row, index) => {
                    const isItemSelected = isSelected(row.productName);
                    const labelId = `enhanced-table-checkbox-${index}`;

                    return (
                      <TableRow
                        hover
                        onClick={event => handleClick(event, row)}
                        role="checkbox"
                        aria-checked={isItemSelected}
                        tabIndex={-1}
                        key={`${row.productName}_${index}`}
                        selected={isItemSelected}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            color="primary"
                            checked={isItemSelected}
                            inputProps={{
                              'aria-labelledby': labelId
                            }}
                          />
                        </TableCell>
                        <TableCell
                          component="th"
                          id={labelId}
                          scope="row"
                          padding="none">
                          <Grid container>
                            <Grid item>
                              <ImageCell logo={row.image} />
                            </Grid>
                            <Grid
                              item
                              style={{ marginLeft: 10, paddingTop: 10 }}>
                              <span>{row.productName}</span>
                            </Grid>
                          </Grid>
                        </TableCell>
                        <TableCell component="th" scope="row">
                          {pointOfSales[row.storeName] ? (
                            <ImageCell
                              logo={pointOfSales[row.storeName].logo}
                              fillContainer={true}
                            />
                          ) : null}
                        </TableCell>
                        <TableCell component="th" id={labelId} scope="row">
                          <StockStatus inStock={row.inStock} />
                        </TableCell>
                        <TableCell component="th" id={labelId} scope="row">
                          {row.price}
                        </TableCell>
                        <TableCell component="th" id={labelId} scope="row">
                          {row.bundleName}
                        </TableCell>
                        <TableCell component="th" id={labelId} scope="row">
                          {row.weight}
                        </TableCell>
                        <TableCell component="th" id={labelId} scope="row">
                          {row.volume}
                        </TableCell>
                        <TableCell component="th" id={labelId} scope="row">
                          {row.depth}
                        </TableCell>
                        <TableCell component="th" id={labelId} scope="row">
                          {row.snoozeUntil ? (
                            <div 
                              style={{ 
                                display: 'flex', 
                                flexDirection: 'row',
                                gap: 12,
                                alignItems: 'center'
                              }} 
                              onClick={e => e.stopPropagation()}>
                              <Button 
                                size="small" 
                                variant="outlined" 
                                color="error"
                                onClick={() => unsnoozeRow(row)}
                                sx={{ 
                                  textTransform: 'none',
                                  fontSize: 12,
                                  padding: '4px 12px',
                                  minWidth: 'auto',
                                  borderColor: '#d32f2f',
                                  color: '#d32f2f',
                                  '&:hover': {
                                    borderColor: '#b71c1c',
                                    backgroundColor: 'rgba(211, 47, 47, 0.04)'
                                  }
                                }}>
                                Unsnooze
                              </Button>
                              <span style={{ 
                                fontSize: 12, 
                                color: '#666',
                                fontWeight: 500,
                                lineHeight: 1,
                                whiteSpace: 'nowrap'
                              }}>
                                Snoozed until {new Date(row.snoozeUntil).toLocaleString('en-GB', { 
                                  day: '2-digit', 
                                  month: '2-digit', 
                                  year: '2-digit',
                                  hour: '2-digit', 
                                  minute: '2-digit',
                                  hour12: false
                                }).replace(',', '')}
                              </span>
                            </div>
                          ) : (
                            <Button 
                              size="small" 
                              variant="outlined" 
                              onClick={(e) => { e.stopPropagation(); openSnoozeForRow(row); }}
                              sx={{ 
                                textTransform: 'none',
                                fontSize: 12,
                                padding: '4px 12px'
                              }}>
                              Snooze
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
            </TableBody>
          </Table>
        </TableContainer>
        {products && products.length > 50 && (
          <div style={{ padding: 20, borderTop: '1px solid #efefef' }}>
            <Pagination
              count={Math.ceil(products.length / 50)}
              variant="outlined"
              shape="rounded"
              page={page}
              onChange={(evt, val) => {
                setPage(val);
              }}
            />
          </div>
        )}

        {selected.length === 1 && !showEditItems && (
          <EditFieldsButton
            numbToEdit={selected.length}
            onClick={() => {
              setShowEditItems(true);
            }}
          />
        )}
        {showEditItems && (
          <UpdateItem
            edit
            products={selected}
            onClose={() => {
              setShowEditItems(false);
            }}
            partners={partners}
            pointOfSales={pointOfSales}
            onSubmit={onEditProduct}
          />
        )}
        {showAddItem && (
          <UpdateItem
            onClose={() => {
              setShowAddItem(false);
            }}
            partners={partners}
            pointOfSales={pointOfSales}
            onSubmit={onAddProduct}
          />
        )}
        {filterType === BUNDLE_FILTER && (
          <BundleFilter
            bundles={bundles}
            selectedBundles={selectedBundles}
            onChange={bundles => {
              setSelectedBundles(bundles);
            }}
            onClose={closeFilter}
            runFilter={runFilter}
          />
        )}
        {filterType === STOCK_FILTER && (
          <StockFilter
            onChange={stockValues => {
              setSelectedStockFilters(stockValues);
            }}
            selectedStockFilters={selectedStockFilters}
            onClose={closeFilter}
            runFilter={runFilter}
          />
        )}
        {filterType === STORE_FILTER && (
          <StoreFilter
            stores={pointOfSales}
            selectedStores={selectedStores}
            onChange={stores => {
              setSelectedStores(stores);
            }}
            onClose={closeFilter}
            runFilter={runFilter}
          />
        )}
        {filterType === PRICE_FILTER && (
          <PriceFilter
            onChange={prices => {
              setSelectedPriceValues(prices);
            }}
            selectedPriceValues={selectedPriceValues}
            onClose={closeFilter}
            runFilter={runFilter}
          />
        )}
        {filterType === WEIGHT_FILTER && (
          <WeightFilter
            onChange={weightValues => {
              setSelectedWeightFilters(weightValues);
            }}
            selectedWeightFilters={selectedWeightFilters}
            onClose={closeFilter}
            runFilter={runFilter}
          />
        )}
        {filterType === SNOOZE_FILTER && (
          <SnoozeFilter
            onChange={snoozeValues => {
              setSelectedSnoozeFilters(snoozeValues);
            }}
            selectedSnoozeFilters={selectedSnoozeFilters}
            onClose={closeFilter}
            runFilter={runFilter}
          />
        )}
        {notification && (
          <Notify type={notification.type} message={notification.message} />
        )}
        <Dialog 
          open={isSnoozeOpen} 
          onClose={() => setIsSnoozeOpen(false)}
          maxWidth="xs"
          fullWidth>
          <DialogTitle sx={{ pb: 1 }}>
            Snooze Item
            {snoozeTarget && (
              <div style={{ fontSize: 14, color: '#666', fontWeight: 400, marginTop: 4 }}>
                {snoozeTarget.productName}
              </div>
            )}
          </DialogTitle>
          <DialogContent>
            <Stack spacing={1.5} sx={{ mt: 2, mb: 1 }}>
              {snoozeOptions.map((option) => (
                <Button 
                  key={option.value} 
                  variant="contained"
                  color="primary"
                  onClick={() => submitSnoozeForRow(option)}
                  sx={{
                    textTransform: 'none',
                    justifyContent: 'flex-start',
                    paddingLeft: 2
                  }}>
                  {option.label}
                </Button>
              ))}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setIsSnoozeOpen(false)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
          </DialogActions>
        </Dialog>
      </Paper>
    </div>
  );
}
